import React from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdEdit, MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';

import RouteTemplateBuilderDialog, { EditableTemplate } from '../components/RouteTemplateBuilderDialog';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import { useRooftop } from '@store';
import { useShellTabActions } from '@features/products/shell/shellTabActions';
import { useGetRouteTemplatesQuery } from '../queries/getRouteTemplates.query.generated';
import { useDeleteRouteTemplateMutation } from '../queries/routeTemplateMutations.generated';

const stopCount = (stops: any): number => (Array.isArray(stops) ? stops.length : 0);

/** The Templates tab of the Parts Delivery shell: the saved route templates. New Template is the
 *  product's creation method in the header; this tab's dialog instance edits. */
const TemplatesTab: React.FC = () => {
  const rooftop = useRooftop();
  const customerId = rooftop?.id || 0;
  // Editing/deleting a saved template is managing existing content; gated on the Parts Delivery
  // product's manage capability (a read_only product shows templates for viewing only).
  const canManage = capabilityGate(useProductEntry('parts-delivery'), 'manageExisting').kind === 'render';

  const { data, loading, refetch } = useGetRouteTemplatesQuery({
    variables: { customerId },
    skip: !customerId,
  });
  const [deleteTemplate] = useDeleteRouteTemplateMutation();

  const templates = data?.route_templates || [];

  // The tab's contribution to the header: its refresh.
  const tabActions = React.useMemo(() => ({ sync: refetch }), [refetch]);
  useShellTabActions(tabActions);

  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<EditableTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<any | null>(null);
  const [deleting, setDeleting] = React.useState(false);


  const handleEdit = (t: any) => {
    setEditing({ id: t.id, name: t.name, stops: t.stops });
    setBuilderOpen(true);
  };

  const handleDelete = (t: any) => setConfirmDelete(t);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteTemplate({ variables: { id: confirmDelete.id } });
      toast.success('Template deleted', { toastId: 'parts-delivery-template-delete' });
      setConfirmDelete(null);
      refetch();
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      toast.error(`Failed to delete template: ${error?.message || 'Unknown error'}`, { toastId: 'parts-delivery-template-delete' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Route Templates
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Reusable destination sets for recurring routes — load one on the run builder, then add the day's orders.
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                No templates yet. Click "New Template" to create your first recurring route.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {['Name', 'Stops', 'Times Used', ''].map((h, i) => (
                    <TableCell
                      key={h || 'actions'}
                      align={i === 1 || i === 2 ? 'center' : 'left'}
                      sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((t: any) => (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography variant='body2'>{stopCount(t.stops)}</Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Typography variant='body2' color='text.secondary'>
                        {t.runs_aggregate?.aggregate?.count ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      {canManage ? (
                        <>
                          <Tooltip title='Edit'>
                            <IconButton size='small' onClick={() => handleEdit(t)}>
                              <MdEdit size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Delete'>
                            <IconButton size='small' onClick={() => handleDelete(t)} sx={{ color: 'error.main' }}>
                              <MdDelete size={18} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </Box>

      <RouteTemplateBuilderDialog
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        onSaved={() => refetch()}
        template={editing}
      />

      <Dialog open={!!confirmDelete} onClose={() => !deleting && setConfirmDelete(null)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete template?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            Delete <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>{confirmDelete?.name}</Box>? This can’t be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant='outlined'
            onClick={() => setConfirmDelete(null)}
            disabled={deleting}
            sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={doDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color='inherit' /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Deleting…' : 'Delete template'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplatesTab;
